/**
 * Centralised money formatting + currency-symbol resolution.
 *
 * Sweep P (2026-05): hoisted out of `pages/payroll-runs/statusChip.tsx` so
 * non-payroll-runs pages (employees, FnF, arrears, ESS, salary-structures,
 * etc.) can render entity-aware currency without round-tripping through the
 * payroll-runs module. `statusChip.tsx` now re-exports from here for
 * back-compat with existing call sites.
 *
 * Usage:
 *   import { fmtMoney, currencySymbolFor, useCurrencySymbol } from '@/lib/money';
 *   const sym = useCurrencySymbol(employee.entity_id);
 *   <span>{fmtMoney(value, sym)}</span>
 *
 * Legacy callers that haven't been migrated keep using `fmtINR(v)` (≡ ₹).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './api-client';

/** ISO 4217 → typical glyph fallback. Mirrors the backend resolver in
 *  payroll_files.py and the RoundingConfig hook. Keep in sync. */
const _CURRENCY_GLYPH: Record<string, string> = {
  INR: '₹',
  USD: '$',
  AED: 'AED ',
  SAR: 'SAR ',
  OMR: 'OMR ',
  GBP: '£',
  EUR: '€',
};

/** Locale to use for grouping based on currency. INR keeps the lakh/crore
 *  grouping; others fall back to en-US thousands. */
function _localeFor(symbol: string): string {
  return symbol === '₹' ? 'en-IN' : 'en-US';
}

/** Format a money value with the given currency symbol.
 *  Pass `symbol` resolved from `entity.currency_code` (use
 *  `useCurrencySymbol(entity_id)` in components). Default '₹' is kept ONLY
 *  for legacy call sites — new code should pass the entity's actual symbol
 *  so non-INR entities don't render ₹. */
export type MoneyOpts = {
  /** Fixed number of fraction digits (min == max). Omit → up to 2, trailing
   *  zeros trimmed (the legacy behaviour). Use `precision: 0` for whole-rupee
   *  reference-rate tables (PT slabs, LWF rules, minimum wages). */
  precision?: number;
  /** 'INDIAN' = lakh/crore grouping (en-IN); 'US' = thousands (en-US). Omit →
   *  inferred from the symbol (₹ → INDIAN). */
  format?: 'INDIAN' | 'US';
};

export function fmtMoney(
  v: number | string | null | undefined,
  symbol: string = '₹',
  opts?: MoneyOpts,
): string {
  if (v == null || v === '') return '—';
  const n = typeof v === 'string' ? Number(v) : v;
  if (!isFinite(n)) return '—';
  const locale = opts?.format === 'US' ? 'en-US'
    : opts?.format === 'INDIAN' ? 'en-IN'
    : _localeFor(symbol);
  const nfo: Intl.NumberFormatOptions = opts?.precision != null
    ? { minimumFractionDigits: opts.precision, maximumFractionDigits: opts.precision }
    : { maximumFractionDigits: 2 };
  return `${symbol}${n.toLocaleString(locale, nfo)}`;
}

/** Legacy alias retained so existing imports keep working. New call sites
 *  should switch to `fmtMoney(v, symbol)` to respect entity currency. */
export function fmtINR(v: number | string | null | undefined): string {
  return fmtMoney(v, '₹');
}

/** Hook: an entity-aware money formatter that respects the entity's configured
 *  rounding_precision + number_format + currency_symbol (Payroll Config →
 *  Rounding, with tenant-default fallback). Use on pages that show actual pay
 *  AMOUNTS so they follow the entity's settings. Pass `{ precision: 0 }` at a
 *  specific call site to override (e.g. a whole-rupee cell). Shares the React
 *  Query cache with the Rounding config page, so edits there refresh here. */
export function useMoneyFmt(
  entity_id: string | null | undefined,
): (v: number | string | null | undefined, opts?: MoneyOpts) => string {
  const q = useQuery({
    queryKey: ['entity-payroll-overrides', entity_id],
    queryFn: () => api.get<{
      effective?: { rounding_precision?: number; number_format?: string; currency_symbol?: string };
    }>(`/settings/entities/${entity_id}/payroll-overrides`),
    enabled: !!entity_id,
    staleTime: 5 * 60_000,
  });
  const eff = q.data?.effective;
  const precision = eff?.rounding_precision ?? 2;
  const format: 'INDIAN' | 'US' = eff?.number_format === 'US' ? 'US' : 'INDIAN';
  const symbol = eff?.currency_symbol || '₹';
  return (v, opts) => fmtMoney(v, symbol, {
    precision: opts?.precision ?? precision,
    format: opts?.format ?? format,
  });
}

/** Map ISO 4217 → display glyph used in helper text + headers. */
export function currencySymbolFor(code: string | null | undefined): string {
  if (!code) return '₹';
  return _CURRENCY_GLYPH[code.toUpperCase()] || code.toUpperCase() + ' ';
}

/** Hook: resolve the display symbol for an entity. Returns '₹' until the
 *  entity record loads (and as a permanent fallback if entity_id is null).
 *  Cached via React Query so multiple components sharing an entity_id only
 *  fetch once per session. */
export function useCurrencySymbol(entity_id: string | null | undefined): string {
  const q = useQuery({
    queryKey: ['entities', 'detail', entity_id],
    queryFn: () => api.get<{ currency_code?: string }>(`/entities/${entity_id}`),
    enabled: !!entity_id,
    staleTime: 5 * 60_000,
  });
  return currencySymbolFor(q.data?.currency_code);
}
